import { Pencil, Trash2, Unplug } from 'lucide-react';
import type {
  RoutingBreakerRecord,
  RoutingSkuTargetRecord,
  RoutingTargetDisplayNamesResponse,
  TargetProbeLayer,
  TargetProbeStateRecord,
} from '@nadoutong/contracts';
import type { LegSkuArming } from '../guaranteed/legModel.ts';
import type { TargetLegs } from './probeDot.ts';
import { ProbeStatusDot } from './ProbeStatusDot.tsx';
import { TryProbeButton } from './TryProbeButton.tsx';
import { disabledDetail } from './disabledNote.ts';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, IconButton, Td, Tooltip, Tr } from '../../ui/index.ts';
import { BreakerStateChip } from '../RoutingBreakersDrawer.tsx';
import { TenantRoutingTargetModel, TenantRoutingTargetSupply } from './TenantRoutingTargetSupply.tsx';
import type { TargetShare } from './firstAttemptShare.ts';

interface Props {
  readonly target: RoutingSkuTargetRecord;
  readonly displayNames?: RoutingTargetDisplayNamesResponse;
  /** This target's breaker entry, or `null` when it is not tripped. */
  readonly breaker: RoutingBreakerRecord | null;
  /** Probe state for this target row, or `null` when never probed. */
  readonly probe: TargetProbeStateRecord | null;
  /** Physical-leg state by region, mapped once per page by target id. */
  readonly probeLegs: TargetLegs | null;
  /** Whether this SKU's guaranteed-center mechanisms can actually demote a leg. */
  readonly arming: LegSkuArming;
  /** Routing layer of this pool's rows, for the 试打 shot. */
  readonly probeLayer: TargetProbeLayer;
  /** This target's first-attempt share, or `null` if the pool did not size it. */
  readonly share: TargetShare | null;
  readonly deleting: boolean;
  readonly resettingBreaker: boolean;
  readonly onEdit: (target: RoutingSkuTargetRecord) => void;
  readonly onDelete: (target: RoutingSkuTargetRecord) => void;
  readonly onBreakerReset: (record: RoutingBreakerRecord) => void;
  readonly onBreakerTrip: (target: RoutingSkuTargetRecord) => void;
}

export function TenantRoutingSkuPoolRow({
  target,
  displayNames,
  breaker,
  probe,
  probeLegs,
  arming,
  probeLayer,
  share,
  deleting,
  resettingBreaker,
  onEdit,
  onDelete,
  onBreakerReset,
  onBreakerTrip,
}: Props): JSX.Element {
  const t = useT();

  return (
    <Tr data-testid={`tenant-routing-pool-row-${target.id}`}>
      <Td className="align-top">
        <TenantRoutingTargetSupply target={target} names={displayNames} />
      </Td>
      <Td className="align-top">
        <TenantRoutingTargetModel target={target} names={displayNames} />
      </Td>
      <Td num className="align-top">
        <div className="flex flex-col items-end gap-1">
          <span className="inline-flex items-center gap-1.5">
            {target.weight === 0 ? (
              <Chip tone="neutral" lamp={false}>{t('tenant_routing.pool.reserve')}</Chip>
            ) : null}
            {target.weight}
          </span>
          {/*
            The raw weight alone never answered "how much of the traffic is
            that" — it only means something against the pool's total, which is
            why the share arrives as a prop rather than being derived here
            (#1495). Only `active` targets get a bar: a reserve target already
            says 「纯兜底」 above, and drawing it an empty 0% bar would imply it
            is in the running for first attempts and simply losing.
          */}
          {share !== null && share.kind === 'active' ? (
            <div
              className="flex w-full flex-col items-end gap-0.5"
              data-testid={`tenant-routing-pool-share-row-${target.id}`}
              title={t('tenant_routing.pool.share_label', {
                channel: target.upstream_channel_slug,
                percent: share.percent,
              })}
            >
              <div className="h-1 w-full overflow-hidden rounded-full bg-line-soft" aria-hidden="true">
                <div className="h-full rounded-full bg-signal" style={{ width: `${String(share.percent)}%` }} />
              </div>
              <span className="font-mono text-micro text-paper">{share.percent}%</span>
            </div>
          ) : null}
        </div>
      </Td>
      <Td num className="align-top">
        {target.retry_order === null
          ? <span className="text-paper-low">{t('tenant_routing.pool.retry_order_auto')}</span>
          : target.retry_order}
      </Td>
      <Td className="align-top">
        <span
          className="inline-flex flex-wrap items-center gap-1.5"
          data-testid={`tenant-routing-pool-status-${target.id}`}
        >
          <ProbeStatusDot targetId={target.id} legs={probeLegs} arming={arming} />
          {target.enabled ? (
            <Chip tone="ok">{t('tenant_routing.status.target_enabled')}</Chip>
          ) : (
            <DisabledTargetChip target={target} onEdit={onEdit} />
          )}
          {breaker !== null ? <BreakerStateChip record={breaker} /> : null}
          {probe !== null ? (
            <Chip
              tone={probe.tripped ? 'bad' : 'ok'}
              lamp={false}
              testId={`tenant-routing-probe-${target.id}`}
              title={[
                probe.next_probe_at === null
                  ? null
                  : t('tenant_routing.probe.next', { at: formatLocalTimestamp(probe.next_probe_at) }),
                probe.backoff_level > 0
                  ? t('tenant_routing.probe.backoff', { level: String(probe.backoff_level) })
                  : null,
              ].filter((part): part is string => part !== null).join(' · ') || undefined}
            >
              {probe.tripped
                ? t('tenant_routing.probe.tripped')
                : t('tenant_routing.probe.ok', {
                  at: probe.last_probe_at === null
                    ? '—'
                    : formatLocalTimestamp(probe.last_probe_at),
                })}
            </Chip>
          ) : null}
        </span>
      </Td>
      <Td className="align-top text-right">
        <span className="inline-flex items-center gap-1">
          <TryProbeButton layer={probeLayer} targetId={target.id} />
          {breaker !== null ? (
            <Button
              size="sm"
              disabled={resettingBreaker}
              onClick={() => onBreakerReset(breaker)}
              data-testid={`tenant-routing-breaker-reset-${target.id}`}
            >
              {t('tenant_routing.breaker.reset')}
            </Button>
          ) : null}
          {breaker === null || breaker.state !== 'forced' ? (
            // Force-out stays reachable for healthy targets (the maintenance
            // use case) and for organically open ones (converts to forced /
            // extends). Forced entries only offer recovery.
            <IconButton
              size="sm"
              label={t('tenant_routing.breaker.trip')}
              onClick={() => onBreakerTrip(target)}
              data-testid={`tenant-routing-breaker-trip-${target.id}`}
            >
              <Unplug aria-hidden="true" className="h-3.5 w-3.5" />
            </IconButton>
          ) : null}
          <IconButton
            size="sm"
            label={t('tenant_routing.action.edit_default')}
            onClick={() => onEdit(target)}
            data-testid={`tenant-routing-target-edit-${target.id}`}
          >
            <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton
            size="sm"
            label={t('tenant_routing.pool.remove_target')}
            disabled={deleting}
            onClick={() => onDelete(target)}
            className="text-bad hover:text-bad"
            data-testid={`tenant-routing-target-delete-${target.id}`}
          >
            <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
          </IconButton>
        </span>
      </Td>
    </Tr>
  );
}

/**
 * 目标停用 plus the operator's removal note (0082): the hover says
 * 「手动摘除 · 备注 · 谁 · 多久前」, the note itself sits beside the chip
 * (truncated) and opens the target editor, where it can be changed.
 */
export function DisabledTargetChip({
  target,
  onEdit,
  nowMs = Date.now(),
}: {
  readonly target: RoutingSkuTargetRecord;
  readonly onEdit: (target: RoutingSkuTargetRecord) => void;
  readonly nowMs?: number;
}): JSX.Element {
  const t = useT();
  const tip = t('tenant_routing.disabled_note.tip', { detail: disabledDetail(target, nowMs, t) });
  const note = target.disabled_note ?? '';
  return (
    <>
      <Tooltip content={tip}>
        <span title={tip} data-testid={`tenant-routing-disabled-chip-${target.id}`}>
          <Chip tone="neutral">{t('tenant_routing.status.target_disabled')}</Chip>
        </span>
      </Tooltip>
      <button
        type="button"
        className="max-w-[140px] truncate text-left text-micro text-paper-low underline decoration-line hover:text-paper-mid"
        title={t('tenant_routing.disabled_note.edit')}
        aria-label={t('tenant_routing.disabled_note.edit')}
        onClick={() => onEdit(target)}
        data-testid={`tenant-routing-disabled-note-${target.id}`}
      >
        {note.length > 0 ? note : t('tenant_routing.disabled_note.none')}
      </button>
    </>
  );
}
